import type {
  DictsysFrenchAdjective,
  DictsysFrenchAdjectiveInflectionScheme,
  DictsysInflectionSchemeAdjectiveGender,
  DictsysInflectionSchemeCell,
  DictsysInflectionSchemeContent,
  DictsysInflectionSchemeTable
} from "@/types";

type GenderType = "masculine" | "feminine";
type AdjectiveType = "comparative" | "positive" | "superlative";

// All of the hacky cases that are, for some reason, different from everything else
const specialCases: string[] = ["beau", "mou", "fou", "vieux", "nouveau"];

function getAdjectives(
  prefix: string,
  comment: string,
  article: DictsysInflectionSchemeAdjectiveGender,
  gender: GenderType,
  lemma: string
): DictsysInflectionSchemeCell[] {
  const cells: DictsysInflectionSchemeCell[] = [{ elements: [{ texts: [prefix], comment }], type: "sub-header" }];

  if (specialCases.includes(lemma) && gender === "masculine") {
    cells.push(getSpecialCaseCell(article[gender]["singular"]));
  } else {
    cells.push(getCell(article[gender]["singular"]));
  }

  cells.push(getCell(article[gender]["plural"]));
  return cells;
}

function getCell(content: DictsysInflectionSchemeContent[]): DictsysInflectionSchemeCell {
  const cell: DictsysInflectionSchemeCell = { type: "body", elements: [] };

  if (content.length < 1) {
    return cell;
  }

  cell.elements.push({ texts: [content[0].index ?? "-"] });
  return cell;
}

function getSpecialCaseCell(content: DictsysInflectionSchemeContent[]): DictsysInflectionSchemeCell {
  const cell: DictsysInflectionSchemeCell = { type: "body", elements: [] };

  if (content.length < 2) {
    return cell;
  }

  cell.elements.push({ texts: [content[0].index], tooltip: "Foran konsonant" });
  cell.elements.push({ texts: [content[1].index], tooltip: "Foran vokal og stumt h" });
  return cell;
}

function getAdjectiveScheme(
  adjective: DictsysFrenchAdjective,
  type: AdjectiveType,
  lemma: string
): DictsysInflectionSchemeTable | null {
  const article: DictsysInflectionSchemeAdjectiveGender = adjective[type];

  const definite = getAdjectives("Maskulinum", "(hankøn)", article, "masculine", lemma);
  const indefinite = getAdjectives("Femininum", "(hunkøn)", article, "feminine", lemma);

  if (definite.length < 1 && indefinite.length < 1) {
    return null;
  }

  const table: DictsysInflectionSchemeTable = { header: "", cells: [] };

  table.header = {
    positive: "Positiv",
    comparative: "Komparativ",
    superlative: "Superlativ"
  }[type];

  table.cells = [
    [
      { type: "sub-header", elements: [{}] },
      { elements: [{ texts: ["Singularis"], comment: "(ental)" }], type: "sub-header" },
      { elements: [{ texts: ["Pluralis"], comment: "(flertal)" }], type: "sub-header" }
    ],
    definite,
    indefinite
  ];

  return table;
}

export function getTables(
  inflectionScheme: DictsysFrenchAdjectiveInflectionScheme,
  lemma: string
): DictsysInflectionSchemeTable[] {
  if (!inflectionScheme.adjective) {
    return [];
  }

  const tables: DictsysInflectionSchemeTable[] = [];

  const positive = getAdjectiveScheme(inflectionScheme.adjective, "positive", lemma);
  if (positive) {
    tables.push(positive);
  }

  const comparative = getAdjectiveScheme(inflectionScheme.adjective, "comparative", lemma);
  if (comparative) {
    tables.push(comparative);
  }

  const superlative = getAdjectiveScheme(inflectionScheme.adjective, "superlative", lemma);
  if (superlative) {
    tables.push(superlative);
  }

  return tables;
}
